import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { styled, useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import MuiDrawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import {
  Collapse,
} from '@mui/material'
import { HomeIcon, ResourcesIcon, SearchIcon, SupportIcon, CheckIcon, SettingsIcon, UserManagementIcon, ReportIcon } from 'assets'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
const items = [
  {
    path: '/',
    title: 'Home',
    icon: HomeIcon,
  },
  {
    path: '/golden-record',
    title: 'Your Golden Records',
    icon: CheckIcon,
  },
  {
    path: '/find',
    title: 'Find a Business',
    icon: SearchIcon,
  },
  {
    path: '/resources',
    title: 'Partner Applications',
    icon: ResourcesIcon,
  },
  {
    title: 'Settings',
    icon: SettingsIcon,
    children: [
      {
        title: 'System Level Settings',
        children: [
          {
            path: '/system-level-settings/match-confidence',
            title: 'Markaaz Match Confidence Threshold',
          },
          {
            path: '/system-level-settings/contract-terms',
            title: 'Contract Terms',
          },
        ]
      },
      {
        title: 'Batch Setup',
        children: [
          {
            path: '/system-level-settings/batch/file-validation',
            title: 'Field Mapping & Test File Validation',
          },
        ]
      },
      {
        title: 'Business Decisioning & P2B',
        children: [
          {
            path: '/system-level-settings/decisioning',
            title: 'Business Decisioning',
          },
          {
            path: '/system-level-settings/p2b',
            title: 'P2B',
          },
        ]
      },

    ],
  },
  {
    path: '/user-management',
    title: 'User Management',
    icon: UserManagementIcon,
  },
  {
    title: 'Reports',
    icon: ReportIcon,
    children: [
      {
        path: '/monitoring-reports',
        title: 'Monitoring Reports',
      },
      {
        path: '/batch-summary',
        title: 'Batch Summary Reports',
      },
    ]
  },
  {
    path: 'https://support.markaaz.com/knowledge?_gl=1*2nu3op*_ga*ODIxNjA5MDA4LjE2NTA1ODEzODM.*_ga_ERS8LY82HR*MTY2MDMxNDc5MS4xOTEuMC4xNjYwMzE0NzkxLjYw',
    target: '_blank',
    title: 'Support Center',
    icon: SupportIcon,
  },
]
const drawerWidth = 245

const openedMixin = theme => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  scrollbarWidth: 'none',
})

const closedMixin = theme => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  scrollbarWidth: 'none',
})

const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '.MuiDrawer-paper': {
    border: 'none',
    background: theme.palette.background.sidebar,
    color: 'white',
  },
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}))

export default function SideBar(props) {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState({})

  const toggleMenu = title => {
    setExpandedMenus(prev => ({ ...prev, [title]: !prev[title] }))
  }

  const isParentActive = menuItem => {
    if (location.pathname === menuItem.path) return true
    if (!menuItem.children) return false

    return menuItem.children.some(
      child => location.pathname === child.path || isParentActive(child),
    )
  }

  const renderMenuItems = (menuItems, level = 0) =>
    menuItems.map(item => {
      const isActive = isParentActive(item);

      return (
        <Box key={item.title}>
          <ListItem disablePadding sx={{
            display: 'block',
            paddingLeft: level * 2 + (item.icon ? 0 : 2)
          }}>
            <ListItemButton
              onClick={() =>
                item.children
                  ? toggleMenu(item.title)
                  : item.target
                    ? window.open(item.path, item.target)
                    : navigate(item.path)
              }
              sx={{
                minHeight: 50,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                background: level === 0 && isActive ? theme.palette.background.primary : 'transparent',
                color: level !== 0 && isActive ? theme.palette.text.aqua : theme.palette.text.white
              }}
            >
              {item.icon && (
                <ListItemIcon sx={{ minWidth: 0, mr: open ? 1 : 'auto', justifyContent: 'center' }}>
                  <item.icon
                    style={{
                      transform: 'scale(0.75)',
                      filter:
                        isActive ? 'brightness(2) sepia(0.3) hue-rotate(90deg) saturate(1)' : '',
                    }}
                  />
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.title}
                sx={{
                  opacity: open ? 1 : 0,
                  ...(level !== 0 && {
                    '& .MuiTypography-root': {
                      fontSize: '14px'
                    }
                  }),
                  ...(level !== 0 && open && {
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflow: 'hidden',
                  })
                }} />
              {item.children && open ? expandedMenus[item.title] ? <ExpandLess /> : <ExpandMore /> : null}
            </ListItemButton>
          </ListItem>
          {item.children && open && (
            <Collapse in={expandedMenus[item.title]} timeout="auto" unmountOnExit>
              <List>{renderMenuItems(item.children, level + 1)}</List>
            </Collapse>
          )}
        </Box>
      )
    })

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        open={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => { setOpen(false); setExpandedMenus({}); }}
        sx={{
          background: theme.palette.background.sidebar,
          zIndex: theme.zIndex.drawer
        }}
      >
        <List sx={{ paddingTop: '100px' }}>{renderMenuItems(items)}</List>
      </Drawer>
    </Box>
  )
}
